import React, { useState, useEffect, useCallback } from "react";
import { audit as auditApi, recycleBin as recycleBinApi, loginHistory as loginHistoryApi, security as securityApi, projects as projectsApi } from "../services/api";
import toast from "react-hot-toast";
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiClock, FiUser, FiActivity, FiX, FiTrash2, FiRefreshCw, FiSettings, FiLogIn, FiMonitor, FiSmartphone, FiTablet, FiGlobe, FiShield, FiCheckCircle, FiAlertTriangle, FiXCircle, FiInfo, FiPlayCircle, FiSave } from "react-icons/fi";

const MODULE_LABELS = {
  reservations: "الحجوزات",
  clients: "العملاء",
  client_groups: "مجموعات العملاء",
  developers: "المطورين",
  marketers: "المسوقين",
  marketer_groups: "مجموعات المسوقين",
  prospects: "المحتملين",
  prospect_groups: "مجموعات المحتملين",
  projects: "المشاريع",
  users: "المستخدمين",
  profiles: "البروفايلات",
  settings: "الإعدادات",
  disbursements: "صرف الدلالات",
  whatsapp: "واتساب",
  client_comm: "تواصل العملاء",
  marketer_comm: "تواصل المسوقين",
  business_comm: "التواصل المؤسسي",
  contracts: "العقود",
};

// v3.3.362 — 9 تابات حسب الأقسام
const MODULE_GROUPS = {
  reservations:  { label: "📅 الحجوزات",         modules: ["reservations"],                                  color: "#2563eb" },
  projects:      { label: "🏗️ المشاريع",          modules: ["projects"],                                      color: "#0891b2" },
  disbursements: { label: "💰 صرف الدلالات",     modules: ["disbursements"],                                 color: "#16a34a" },
  comm:          { label: "📞 التواصل والواتس",  modules: ["whatsapp", "client_comm", "marketer_comm", "business_comm"], color: "#7c3aed" },
  clients:       { label: "👥 العملاء",            modules: ["clients", "client_groups"],                      color: "#059669" },
  marketers:     { label: "🤝 المسوقين",          modules: ["marketers", "marketer_groups"],                  color: "#d97706" },
  developers:    { label: "🏢 المطورين",          modules: ["developers"],                                    color: "#7c3aed" },
  prospects:     { label: "🎯 المحتملين",         modules: ["prospects", "prospect_groups"],                  color: "#ec4899" },
  other:         { label: "⚙️ الأخرى",            modules: ["users", "profiles", "settings", "contracts"],   color: "#64748b" },
};

// v3.3.544 — Entity type labels + icons for recycle bin
const RECYCLE_ENTITY_LABELS = {
  reservation: { label: "حجز", icon: "📅", color: "#2563eb" },
  client: { label: "عميل", icon: "👤", color: "#059669" },
  client_group: { label: "مجموعة عملاء", icon: "👥", color: "#059669" },
  developer: { label: "مطور", icon: "🏢", color: "#7c3aed" },
  marketer: { label: "مسوق", icon: "🤝", color: "#d97706" },
  marketer_group: { label: "مجموعة مسوقين", icon: "👥", color: "#d97706" },
  prospect: { label: "محتمل", icon: "🎯", color: "#ec4899" },
  prospect_group: { label: "مجموعة محتملين", icon: "🎯", color: "#ec4899" },
  project: { label: "مشروع", icon: "🏗️", color: "#0891b2" },
  unit: { label: "وحدة", icon: "🏠", color: "#0891b2" },
  building: { label: "مبنى", icon: "🏬", color: "#0891b2" },
  disbursement: { label: "طلب صرف", icon: "💰", color: "#16a34a" },
  contract: { label: "عقد", icon: "📄", color: "#4f46e5" },
};

// قائمة كل modules المُصنّفة (لمعرفة "غير المُصنّف" → يدخل في "الأخرى")
const ALL_NON_OTHER_MODULES = new Set(
  Object.entries(MODULE_GROUPS).filter(([k]) => k !== "other").flatMap(([, g]) => g.modules)
);

const ACTION_LABELS = {
  create: "إنشاء",
  update: "تعديل",
  delete: "حذف",
  import: "استيراد",
  export: "تصدير",
  login: "تسجيل دخول",
  logout: "تسجيل خروج",
  assign_profile: "تعيين بروفايل",
  bulk_delete: "حذف جماعي",
  // v3.3.616 — عمليات الوحدات
  update_unit: "تعديل وحدة", delete_unit: "حذف وحدة", update_unit_price: "سعر جديد للوحدة", delete_unit_price: "حذف سعر من سجل الوحدة", /* v3.4.843 */
  add_unit_payment: "إضافة دفعة", delete_unit_payment: "حذف دفعة",
  add_scheduled_payment: "إضافة دفعة مجدولة", delete_scheduled_payment: "حذف دفعة مجدولة",
  mark_scheduled_paid: "سداد دفعة مجدولة", unmark_scheduled_paid: "إلغاء سداد دفعة",
  link_reservation_unit: "ربط حجز بوحدة", unlink_reservation_unit: "فك ربط حجز",
  update_resale_deal: "صفقة إعادة بيع", update_payment_agreement: "تعديل اتفاقية السداد",
  update_costs: "تعديل التكاليف",
};

const ACTION_COLORS = {
  create: "#22c55e",
  update: "#3b82f6",
  delete: "#ef4444",
  import: "#8b5cf6",
  export: "#06b6d4",
  login: "#10b981",
  logout: "#64748b",
  assign_profile: "#f59e0b",
  bulk_delete: "#dc2626",
};

const MODULE_COLORS = {
  reservations: "#2563eb",
  clients: "#059669",
  developers: "#7c3aed",
  marketers: "#d97706",
  projects: "#0891b2",
  users: "#e11d48",
  profiles: "#4f46e5",
  settings: "#64748b",
};

const FIELD_LABELS = {
  serial_number: "رقم التسلسل", date: "التاريخ", operation_type: "نوع العملية",
  client_name: "العميل", client_type: "نوع العميل", developer: "المطور",
  unit_type: "الوحدة", readiness: "الجاهزية", value: "القيمة",
  deposit_amount: "العربون", deposit_status: "حالة العربون", bank_account: "الحساب",
  dalala_amount: "الدلالة", operation_status: "حالة الحجز", marketer: "المسوق",
  notes: "ملاحظات", ext_marketer_share: "حصة المسوق الخارجي",
  int_marketer_share: "حصة المسوق الداخلي", waha_share: "حصة واحة",
  sales_mgr_share: "حصة مدير المبيعات", mkt_mgr_share: "حصة مدير التسويق",
  company_share: "حصة الشركة", commission_status_res: "حالة الدلالة (إدارة)",
  commission_status_acc: "حالة الدلالة (محاسبة)", notes_extra: "ملاحظات إضافية",
  purchase_purpose: "هدف الشراء", confirmed: "مؤكد",
  // v3.3.611 — بقية حقول الحجز (نِسَب التوزيع + الحصص + السعر + اختيار الوحدة)
  ext_pct: "نسبة المسوق الخارجي", int_pct: "نسبة المسوق الداخلي",
  marketer1_pct: "نسبة المسوق 1", marketer2_pct: "نسبة المسوق 2", dalilak_pct: "نسبة دليلك",
  waha_pct: "نسبة واحة", sales_mgr_pct: "نسبة مدير المبيعات", mkt_mgr_pct: "نسبة مدير التسويق",
  company_pct: "نسبة الشركة",
  marketer1_share: "حصة المسوق 1", marketer2_share: "حصة المسوق 2", dalilak_share: "حصة دليلك",
  official_price: "السعر الرسمي", discount: "الخصم", payment_agreement: "اتفاقية السداد",
  selected_project_id: "المشروع", selected_building_id: "المبنى",
  selected_floor: "الدور", selected_unit_code: "كود الوحدة",
  // v3.3.613 — حقول العملاء/المسوقين/المطورين (لعرض تفاصيل التعديل)
  name: "الاسم", phone: "الجوال", office: "المكتب/الشركة", nationality: "الجنسية",
  marketer_classification: "تصنيف المسوق", iban: "الآيبان", bank_name: "البنك",
  id_number: "رقم الهوية", visit_count: "عدد الزيارات", client_number: "رقم العميل",
  classification: "التصنيف", client_category: "فئة العميل", client_stage: "مرحلة العميل",
  is_company_client: "عميل شركة", representative_name: "اسم الممثل",
  representative_phone: "جوال الممثل", is_company_project: "مشروع شركة",
  linked_project_id: "المشروع المرتبط",
};

const sty = {
  badge: (bg) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10,
    fontWeight: 600, background: bg + "18", color: bg, whiteSpace: "nowrap",
  }),
  td: { padding: "10px 12px", fontSize: 12, borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
  th: {
    padding: "10px 12px", fontSize: 10, fontWeight: 700, color: "#64748b",
    background: "#f8fafc", borderBottom: "2px solid #e2e8f0", textAlign: "right",
    whiteSpace: "nowrap", letterSpacing: 0.5,
  },
  // v3.3.613 — per-column filter row
  thFilter: { padding: "4px 8px 8px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", verticalAlign: "top" },
  colInput: {
    width: "100%", padding: "4px 6px", border: "1px solid #e2e8f0", borderRadius: 5,
    fontFamily: "inherit", fontSize: 11, background: "#fff", boxSizing: "border-box", color: "#475569",
  },
  filterBtn: (active) => ({
    padding: "6px 12px", border: "1px solid " + (active ? "#2563eb" : "#e2e8f0"),
    borderRadius: 6, background: active ? "#eff6ff" : "#fff",
    color: active ? "#2563eb" : "#475569", fontSize: 11, cursor: "pointer",
    fontFamily: "inherit", fontWeight: active ? 600 : 400,
  }),
};

export default function AuditLogPage() {
  // v3.3.544 — Top-level view tab: audit log vs recycle bin
  const [view, setView] = useState("audit"); // "audit" | "recycle"

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ module: "", action: "", search: "", date_from: "", date_to: "", project_id: "", building_id: "", user_id: "", entity_id: "", unit_id: "" });
  const [filterOpts, setFilterOpts] = useState({ modules: [], actions: [], users: [] });
  // v3.3.610 — قوائم المشاريع/المباني للفلترة · v3.3.616 — الوحدات
  const [auditProjects, setAuditProjects] = useState([]);
  const [auditBuildings, setAuditBuildings] = useState([]);
  const [auditUnits, setAuditUnits] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  // v3.3.362 — tab نشط
  const [activeTab, setActiveTab] = useState("all");
  const page = pagination.page;

  const fetchData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      // v3.3.362 — تطبيق activeTab على params
      if (activeTab !== "all") {
        if (activeTab === "other") {
          params.module_not_in = Array.from(ALL_NON_OTHER_MODULES).join(",");
        } else {
          const grp = MODULE_GROUPS[activeTab];
          if (grp) params.module = grp.modules.join(",");
        }
      }
      if (filters.module) params.module = filters.module;  // فلتر يدوي يطغى
      if (filters.action) params.action = filters.action;
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.building_id) params.building_id = filters.building_id;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.entity_id) params.entity_id = filters.entity_id;
      if (filters.unit_id) params.unit_id = filters.unit_id;
      const r = await auditApi.list(params);
      setItems(r.data.items);
      setPagination(r.data.pagination);
    } catch (err) {
      toast.error("فشل تحميل سجل التدقيق");
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  useEffect(() => {
    auditApi.filters().then(r => setFilterOpts(r.data)).catch(() => {});
    auditApi.stats().then(r => setStats(r.data)).catch(() => {});
    // v3.3.610 — قائمة المشاريع للفلترة
    projectsApi.list().then(r => setAuditProjects(r.data?.projects || r.data || [])).catch(() => {});
  }, []);

  // v3.3.610 — مباني المشروع المحدد + v3.3.616 — وحداته (فلتر تتالي)
  useEffect(() => {
    if (!filters.project_id) { setAuditBuildings([]); setAuditUnits([]); return; }
    projectsApi.getBuildings(filters.project_id)
      .then(r => setAuditBuildings(r.data?.buildings || []))
      .catch(() => setAuditBuildings([]));
    projectsApi.get(filters.project_id)
      .then(r => setAuditUnits(r.data?.units || []))
      .catch(() => setAuditUnits([]));
  }, [filters.project_id]);

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    const date = dt.toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" });
    const time = dt.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    return (
      <div>
        <div style={{ fontSize: 12 }}>{date}</div>
        <div style={{ fontSize: 10, color: "#94a3b8" }}>{time}</div>
      </div>
    );
  };

  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return null;
    const entries = Object.entries(changes);
    if (entries.length === 0) return null;
    return (
      <div style={{ marginTop: 8, background: "#f8fafc", borderRadius: 6, padding: 10, border: "1px solid #e2e8f0" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>التغييرات:</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {entries.map(([field, { old: oldVal, new: newVal }]) => (
            <div key={field} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: "#475569", minWidth: 100, flexShrink: 0 }}>
                {FIELD_LABELS[field] || field}
              </span>
              <span style={{ color: "#ef4444", textDecoration: "line-through", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                {String(oldVal ?? "-")}
              </span>
              <span style={{ color: "#94a3b8" }}>←</span>
              <span style={{ color: "#22c55e", fontWeight: 500, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                {String(newVal ?? "-")}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // v3.3.544 — Friendlier metadata labels and value formatting
  const METADATA_LABELS = {
    client_name: "العميل",
    developer: "المطور",
    marketer_id: "المسوق",
    amount: "المبلغ",
    value: "القيمة",
    phone: "الهاتف",
    operation_status: "حالة العملية",
    reservation_id: "رقم الحجز",
    project_id: "المشروع",
    building_id: "المبنى",
    floor_number: "الدور",
    members_count: "عدد الأعضاء",
    deleted_units: "وحدات محذوفة",
    deleted_buildings: "مباني محذوفة",
    moved_to_recycle_bin: "نُقل لسلة المهملات",
    user_count: "عدد المستخدمين",
    retention_days: "أيام الاحتفاظ",
    purged_count: "العدد المحذوف",
    entity_type: "نوع العنصر",
    type: "النوع",
    payment_id: "معرف الدفعة",
    payment_date: "تاريخ الدفعة",
  };

  const fmtMetadataValue = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "boolean") return v ? "✓ نعم" : "✗ لا";
    if (typeof v === "number") return v.toLocaleString("ar-EG");
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const renderMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return null;
    const entries = Object.entries(metadata).filter(([_, v]) => v !== null && v !== undefined && v !== "");
    if (entries.length === 0) return null;
    return (
      <div style={{ marginTop: 8, background: "#fffbeb", borderRadius: 6, padding: 10, border: "1px solid #fde68a" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>تفاصيل إضافية:</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 4 }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{ fontSize: 11, padding: "3px 0", display: "flex", gap: 6 }}>
              <span style={{ color: "#92400e", fontWeight: 600 }}>{METADATA_LABELS[k] || k}:</span>
              <span style={{ color: "#451a03", wordBreak: "break-word" }}>{fmtMetadataValue(v)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const clearFilters = () => setFilters({ module: "", action: "", search: "", date_from: "", date_to: "", project_id: "", building_id: "", user_id: "", entity_id: "", unit_id: "" });
  const hasActiveFilters = filters.module || filters.action || filters.search || filters.date_from || filters.date_to || filters.project_id || filters.building_id || filters.user_id || filters.entity_id || filters.unit_id;

  return (
    <div dir="rtl" style={{ padding: 20, maxWidth: 1200 }}>
      {/* v3.3.544 — Top-level tabs: audit log vs recycle bin vs login history */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, borderBottom: "2px solid #e2e8f0" }}>
        <button onClick={() => setView("audit")} style={{
          padding: "10px 20px",
          background: view === "audit" ? "#fff" : "transparent",
          border: "none",
          borderBottom: view === "audit" ? "3px solid #2563eb" : "3px solid transparent",
          marginBottom: -2,
          color: view === "audit" ? "#2563eb" : "#64748b",
          fontWeight: view === "audit" ? 700 : 500,
          fontSize: 14,
          fontFamily: "inherit",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <FiActivity size={14} /> سجل التنبيهات
        </button>
        <button onClick={() => setView("recycle")} style={{
          padding: "10px 20px",
          background: view === "recycle" ? "#fff" : "transparent",
          border: "none",
          borderBottom: view === "recycle" ? "3px solid #dc2626" : "3px solid transparent",
          marginBottom: -2,
          color: view === "recycle" ? "#dc2626" : "#64748b",
          fontWeight: view === "recycle" ? 700 : 500,
          fontSize: 14,
          fontFamily: "inherit",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <FiTrash2 size={14} /> سلة المهملات
        </button>
        {/* v3.3.546 — Login history tab */}
        <button onClick={() => setView("logins")} style={{
          padding: "10px 20px",
          background: view === "logins" ? "#fff" : "transparent",
          border: "none",
          borderBottom: view === "logins" ? "3px solid #16a34a" : "3px solid transparent",
          marginBottom: -2,
          color: view === "logins" ? "#16a34a" : "#64748b",
          fontWeight: view === "logins" ? 700 : 500,
          fontSize: 14,
          fontFamily: "inherit",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <FiLogIn size={14} /> سجل تسجيل الدخول
        </button>
        {/* v3.3.553 — Cybersecurity self-assessment tab */}
        <button onClick={() => setView("security")} style={{
          padding: "10px 20px",
          background: view === "security" ? "#fff" : "transparent",
          border: "none",
          borderBottom: view === "security" ? "3px solid #7c3aed" : "3px solid transparent",
          marginBottom: -2,
          color: view === "security" ? "#7c3aed" : "#64748b",
          fontWeight: view === "security" ? 700 : 500,
          fontSize: 14,
          fontFamily: "inherit",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <FiShield size={14} /> تقييم الأمن السيبراني
        </button>
      </div>

      {view === "recycle" ? <RecycleBinTab /> : view === "logins" ? <LoginHistoryTab /> : view === "security" ? <SecurityComplianceTab /> : (
      <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <FiActivity size={20} style={{ color: "#2563eb" }} /> سجل التنبيهات
          </h1>
          <p style={{ fontSize: 11, color: "#94a3b8", margin: "4px 0 0" }}>
            جميع التعديلات على النظام · {pagination.total} سجل
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowFilters(!showFilters)} style={{
            ...sty.filterBtn(showFilters || hasActiveFilters),
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <FiFilter size={12} /> فلترة
            {hasActiveFilters && <span style={{ background: "#2563eb", color: "#fff", borderRadius: "50%", width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>!</span>}
          </button>
        </div>
      </div>

      {/* v3.3.362 — تابات الأقسام (9 + الكل) */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #e2e8f0", flexWrap: "wrap", overflowX: "auto" }}>
        {[
          { key: "all", label: "📋 الكل", color: "#475569" },
          ...Object.entries(MODULE_GROUPS).map(([k, g]) => ({ key: k, label: g.label, color: g.color })),
        ].map(t => {
          const isActive = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{
                padding: "8px 14px",
                background: isActive ? "#fff" : "transparent",
                border: "none",
                borderBottom: isActive ? `2px solid ${t.color}` : "2px solid transparent",
                marginBottom: -1,
                color: isActive ? t.color : "#64748b",
                fontWeight: isActive ? 700 : 500,
                fontSize: 12,
                fontFamily: "inherit",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          {stats.modules.slice(0, 5).map(m => (
            <div key={m.module} onClick={() => setFilters(f => ({ ...f, module: f.module === m.module ? "" : m.module }))}
              style={{
                flex: "1 1 120px", padding: "10px 14px", background: filters.module === m.module ? (MODULE_COLORS[m.module] || "#64748b") + "12" : "#fff",
                border: "1px solid " + (filters.module === m.module ? (MODULE_COLORS[m.module] || "#64748b") : "#e2e8f0"),
                borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
              }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: MODULE_COLORS[m.module] || "#475569" }}>{m.count}</div>
              <div style={{ fontSize: 10, color: "#94a3b8" }}>{MODULE_LABELS[m.module] || m.module}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Search */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>بحث</label>
              <div style={{ position: "relative", marginTop: 4 }}>
                <FiSearch style={{ position: "absolute", right: 8, top: 9, color: "#94a3b8" }} size={13} />
                <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder="اسم المستخدم، البريد، أو الكيان..."
                  style={{ width: "100%", padding: "7px 28px 7px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, boxSizing: "border-box" }} />
              </div>
            </div>
            {/* Module */}
            <div style={{ flex: "0 0 140px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>القسم</label>
              <select value={filters.module} onChange={e => setFilters(f => ({ ...f, module: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, background: "#fff" }}>
                <option value="">الكل</option>
                {filterOpts.modules.map(m => <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>)}
              </select>
            </div>
            {/* Action */}
            <div style={{ flex: "0 0 140px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>نوع العملية</label>
              <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, background: "#fff" }}>
                <option value="">الكل</option>
                {filterOpts.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
              </select>
            </div>
            {/* Date From */}
            <div style={{ flex: "0 0 140px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>من تاريخ</label>
              <input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, boxSizing: "border-box" }} />
            </div>
            {/* Date To */}
            <div style={{ flex: "0 0 140px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>إلى تاريخ</label>
              <input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, boxSizing: "border-box" }} />
            </div>
            {/* v3.3.610 — Project */}
            <div style={{ flex: "0 0 160px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>المشروع</label>
              <select value={filters.project_id} onChange={e => setFilters(f => ({ ...f, project_id: e.target.value, building_id: "" }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, background: "#fff" }}>
                <option value="">الكل</option>
                {auditProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {/* v3.3.610 — Building (cascades from project) */}
            <div style={{ flex: "0 0 160px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>المبنى</label>
              <select value={filters.building_id} disabled={!filters.project_id}
                onChange={e => setFilters(f => ({ ...f, building_id: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, background: filters.project_id ? "#fff" : "#f1f5f9" }}>
                <option value="">الكل</option>
                {auditBuildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {/* v3.3.616 — Unit (cascades from project/building) */}
            <div style={{ flex: "0 0 150px" }}>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>الوحدة</label>
              <select value={filters.unit_id} disabled={!filters.project_id}
                onChange={e => setFilters(f => ({ ...f, unit_id: e.target.value }))}
                style={{ width: "100%", padding: "7px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12, marginTop: 4, background: filters.project_id ? "#fff" : "#f1f5f9" }}>
                <option value="">الكل</option>
                {auditUnits
                  .filter(u => !filters.building_id || String(u.building_id) === String(filters.building_id))
                  .map(u => <option key={u.id} value={u.id}>{u.unit_label || u.unit_number || ("#" + u.id)}</option>)}
              </select>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters} style={{
                padding: "7px 12px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2",
                color: "#dc2626", fontSize: 11, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4,
              }}><FiX size={12} /> مسح الفلاتر</button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={sty.th}>الوقت</th>
                <th style={sty.th}>المستخدم</th>
                <th style={sty.th}>القسم</th>
                <th style={sty.th}>العملية</th>
                <th style={sty.th}>رقم الحجز</th>
                <th style={sty.th}>التفاصيل</th>
              </tr>
              {/* v3.3.613 — صف فلترة/بحث أعلى كل عمود */}
              <tr>
                <th style={sty.thFilter} />
                <th style={sty.thFilter}>
                  <select value={filters.user_id} onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} style={sty.colInput}>
                    <option value="">الكل</option>
                    {filterOpts.users.map(u => <option key={u.user_id} value={u.user_id}>{u.user_display_name || u.user_email}</option>)}
                  </select>
                </th>
                <th style={sty.thFilter}>
                  <select value={filters.module} onChange={e => setFilters(f => ({ ...f, module: e.target.value }))} style={sty.colInput}>
                    <option value="">الكل</option>
                    {filterOpts.modules.map(m => <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>)}
                  </select>
                </th>
                <th style={sty.thFilter}>
                  <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} style={sty.colInput}>
                    <option value="">الكل</option>
                    {filterOpts.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
                  </select>
                </th>
                <th style={sty.thFilter}>
                  <input value={filters.entity_id} onChange={e => setFilters(f => ({ ...f, entity_id: e.target.value }))} placeholder="رقم..." style={sty.colInput} />
                </th>
                <th style={sty.thFilter}>
                  <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="بحث في التفاصيل..." style={sty.colInput} />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ ...sty.td, textAlign: "center", color: "#94a3b8", padding: 40 }}>جاري التحميل...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ ...sty.td, textAlign: "center", color: "#94a3b8", padding: 40 }}>لا توجد سجلات</td></tr>
              ) : items.map(item => (
                <React.Fragment key={item.id}>
                  <tr onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    style={{ cursor: item.changes ? "pointer" : "default", background: expandedId === item.id ? "#f8fafc" : "transparent" }}>
                    <td style={{ ...sty.td, whiteSpace: "nowrap", minWidth: 100 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiClock size={11} style={{ color: "#94a3b8", flexShrink: 0 }} />
                        {formatDate(item.created_at)}
                      </div>
                    </td>
                    <td style={sty.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <FiUser size={11} style={{ color: "#94a3b8", flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>{item.user_display_name || "النظام"}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>{item.user_email || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td style={sty.td}>
                      <span style={sty.badge(MODULE_COLORS[item.module] || "#64748b")}>
                        {MODULE_LABELS[item.module] || item.module}
                      </span>
                    </td>
                    <td style={sty.td}>
                      <span style={sty.badge(ACTION_COLORS[item.action] || "#64748b")}>
                        {ACTION_LABELS[item.action] || item.action}
                      </span>
                    </td>
                    {/* v3.3.613 — رقم الحجز = entity_id (نفس الرقم # في كشف الحجوزات) */}
                    <td style={{ ...sty.td, whiteSpace: "nowrap" }}>
                      {item.module === "reservations" && item.entity_id
                        ? <span style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", fontFamily: "monospace" }}>#{item.entity_id}</span>
                        : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                    <td style={sty.td}>
                      <div>
                        {item.entity_label && (
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{item.entity_label}</span>
                        )}
                        {item.entity_id && !item.entity_label && (
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>#{item.entity_id}</span>
                        )}
                        {item.changes && (
                          <span style={{ fontSize: 10, color: "#3b82f6", marginRight: 6 }}>
                            ({Object.keys(item.changes).length} تغيير)
                          </span>
                        )}
                        {renderMetadata(item.metadata)}
                      </div>
                    </td>
                  </tr>
                  {expandedId === item.id && item.changes && (
                    <tr>
                      <td colSpan={6} style={{ padding: "0 12px 12px 12px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        {renderChanges(item.changes)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #f1f5f9", background: "#fafbfc" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              صفحة {page} من {pagination.pages} · {pagination.total} سجل
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button disabled={page <= 1} onClick={() => fetchData(page - 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                <FiChevronRight size={12} /> السابق
              </button>
              <button disabled={page >= pagination.pages} onClick={() => fetchData(page + 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page >= pagination.pages ? "default" : "pointer", opacity: page >= pagination.pages ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                التالي <FiChevronLeft size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// RECYCLE BIN TAB (v3.3.544)
// قسم سلة المهملات: عرض، استعادة، حذف نهائي، إفراغ السلة، إعدادات.
// ═══════════════════════════════════════════════════════════════════════
function RecycleBinTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ entity_type: "", q: "", date_from: "", date_to: "" });
  const [typeCounts, setTypeCounts] = useState([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [showSettings, setShowSettings] = useState(false);
  const [pendingRetention, setPendingRetention] = useState(90);
  const [expandedId, setExpandedId] = useState(null);
  const page = pagination.page;

  const fetchData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 50 };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.q) params.q = filters.q;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await recycleBinApi.list(params);
      setItems(r.data.items || []);
      setPagination({ page: r.data.page, pages: r.data.total_pages, total: r.data.total });
      setTypeCounts(r.data.type_counts || []);
      setRetentionDays(r.data.retention_days || 90);
      setPendingRetention(r.data.retention_days || 90);
    } catch (e) {
      toast.error("فشل تحميل سلة المهملات");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const handleRestore = async (entry) => {
    const label = entry.entity_label || `${entry.entity_type} #${entry.entity_id}`;
    if (!window.confirm(`هل تريد استعادة "${label}"؟\n\nسيُعاد إلى موقعه الأصلي.`)) return;
    try {
      await recycleBinApi.restore(entry.id);
      toast.success(`تمت استعادة "${label}"`);
      fetchData(page);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشلت الاستعادة", { duration: 7000 });
    }
  };

  const handlePurge = async (entry) => {
    const label = entry.entity_label || `${entry.entity_type} #${entry.entity_id}`;
    if (!window.confirm(`حذف نهائي لـ "${label}"؟\n\n⚠️ هذا الإجراء لا يمكن التراجع عنه.`)) return;
    try {
      await recycleBinApi.purge(entry.id);
      toast.success("تم الحذف النهائي");
      fetchData(page);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحذف");
    }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm(`⚠️ إفراغ السلة كاملة (${pagination.total} عنصر)؟\n\nهذا الإجراء لا يمكن التراجع عنه.`)) return;
    if (!window.confirm("هل أنت متأكد تماماً؟ كل العناصر ستُحذف نهائياً.")) return;
    try {
      const r = await recycleBinApi.purgeAll();
      toast.success(`تم حذف ${r.data.purged} عنصر نهائياً`);
      fetchData(1);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحذف");
    }
  };

  const handleSaveSettings = async () => {
    try {
      const r = await recycleBinApi.setSettings(pendingRetention);
      setRetentionDays(r.data.retention_days);
      setShowSettings(false);
      toast.success(`تم حفظ المدة: ${r.data.retention_days} يوم`);
      fetchData(page);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحفظ");
    }
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("ar-EG", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const daysUntilPurge = (purgeAt) => {
    if (!purgeAt) return null;
    const diff = new Date(purgeAt) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#64748b" }}>
          <span>🗑️ <strong>{pagination.total}</strong> عنصر في السلة</span>
          <span style={{ color: "#94a3b8" }}>·</span>
          <span>الاحتفاظ: <strong style={{ color: "#0891b2" }}>{retentionDays}</strong> يوم</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => { setShowSettings(!showSettings); setPendingRetention(retentionDays); }} style={{
            padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff",
            fontSize: 11, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4,
          }}>
            <FiSettings size={12} /> إعدادات
          </button>
          {pagination.total > 0 && (
            <button onClick={handlePurgeAll} style={{
              padding: "6px 12px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2",
              color: "#dc2626", fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 4,
            }}>
              🧹 إفراغ السلة
            </button>
          )}
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "#0f172a" }}>⚙️ إعدادات سلة المهملات</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: "#475569", minWidth: 130 }}>مدة الاحتفاظ بالأيام:</label>
            <input type="number" min="1" max="3650" value={pendingRetention}
              onChange={e => setPendingRetention(parseInt(e.target.value) || 1)}
              style={{ width: 100, padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12 }} />
            <span style={{ fontSize: 11, color: "#94a3b8" }}>يوم (بين 1 و 3650)</span>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10, lineHeight: 1.6 }}>
            • العناصر الأقدم من هذه المدة تُحذف نهائياً تلقائياً (كل ساعة).<br />
            • التغيير يُطبَّق على العناصر الجديدة وعند الفحص التلقائي القادم.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={handleSaveSettings} style={{
              padding: "6px 14px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 6,
              fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
            }}>حفظ</button>
            <button onClick={() => setShowSettings(false)} style={{
              padding: "6px 14px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 6,
              fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            }}>إلغاء</button>
          </div>
        </div>
      )}

      {/* Type filter chips */}
      {typeCounts.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          <button onClick={() => setFilters(f => ({ ...f, entity_type: "" }))} style={{
            padding: "4px 10px", border: "1px solid " + (filters.entity_type === "" ? "#0891b2" : "#e2e8f0"),
            borderRadius: 20, background: filters.entity_type === "" ? "#ecfeff" : "#fff",
            color: filters.entity_type === "" ? "#0891b2" : "#64748b",
            fontSize: 11, cursor: "pointer", fontFamily: "inherit",
            fontWeight: filters.entity_type === "" ? 700 : 500,
          }}>
            📋 الكل ({pagination.total})
          </button>
          {typeCounts.map(tc => {
            const meta = RECYCLE_ENTITY_LABELS[tc.entity_type] || { label: tc.entity_type, icon: "📦", color: "#64748b" };
            const isActive = filters.entity_type === tc.entity_type;
            return (
              <button key={tc.entity_type} onClick={() => setFilters(f => ({ ...f, entity_type: tc.entity_type }))} style={{
                padding: "4px 10px", border: "1px solid " + (isActive ? meta.color : "#e2e8f0"),
                borderRadius: 20, background: isActive ? meta.color + "15" : "#fff",
                color: isActive ? meta.color : "#64748b",
                fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                fontWeight: isActive ? 700 : 500,
              }}>
                {meta.icon} {meta.label} ({tc.c})
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 12, position: "relative" }}>
        <FiSearch size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
        <input type="text" value={filters.q}
          onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          placeholder="بحث بالاسم، رقم العنصر، أو من قام بالحذف..."
          style={{ width: "100%", padding: "8px 32px 8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12 }} />
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🗑️</div>
            <div style={{ fontSize: 13 }}>السلة فارغة</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>كل عناصر الحذف ستظهر هنا للاسترجاع</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={sty.th}>الوقت</th>
                  <th style={sty.th}>النوع</th>
                  <th style={sty.th}>العنصر</th>
                  <th style={sty.th}>حذف بواسطة</th>
                  <th style={sty.th}>متبقي</th>
                  <th style={sty.th}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {items.map(entry => {
                  const meta = RECYCLE_ENTITY_LABELS[entry.entity_type] || { label: entry.entity_type, icon: "📦", color: "#64748b" };
                  const daysLeft = daysUntilPurge(entry.purge_at);
                  const isExpanded = expandedId === entry.id;
                  return (
                    <React.Fragment key={entry.id}>
                      <tr style={{ background: isExpanded ? "#f8fafc" : "#fff" }}>
                        <td style={sty.td}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 11, color: "#475569" }}>{fmtDate(entry.deleted_at)}</span>
                          </div>
                        </td>
                        <td style={sty.td}>
                          <span style={sty.badge(meta.color)}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                        <td style={sty.td}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                            {entry.entity_label || `#${entry.entity_id}`}
                          </div>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>ID: {entry.entity_id}</div>
                        </td>
                        <td style={sty.td}>
                          <div style={{ fontSize: 11, color: "#475569" }}>{entry.deleted_by_name || "—"}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>{entry.deleted_by_email}</div>
                        </td>
                        <td style={sty.td}>
                          {daysLeft !== null && (
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              color: daysLeft <= 7 ? "#dc2626" : daysLeft <= 30 ? "#d97706" : "#16a34a",
                            }}>
                              {daysLeft > 0 ? `${daysLeft} يوم` : "ينتهي اليوم"}
                            </span>
                          )}
                        </td>
                        <td style={sty.td}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => handleRestore(entry)} title="استعادة" style={{
                              padding: "5px 10px", background: "#dcfce7", color: "#15803d", border: "none", borderRadius: 5,
                              fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
                              display: "flex", alignItems: "center", gap: 4,
                            }}>
                              <FiRefreshCw size={11} /> ♻️ استعادة
                            </button>
                            <button onClick={() => handlePurge(entry)} title="حذف نهائي" style={{
                              padding: "5px 8px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 5,
                              fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                            }}>
                              <FiTrash2 size={11} />
                            </button>
                            <button onClick={() => setExpandedId(isExpanded ? null : entry.id)} title="تفاصيل" style={{
                              padding: "5px 8px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 5,
                              fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                            }}>
                              {isExpanded ? "▲" : "▼"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <RecycleBinDetailRow entryId={entry.id} colSpan={6} />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #f1f5f9", background: "#fafbfc" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              صفحة {page} من {pagination.pages} · {pagination.total} عنصر
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button disabled={page <= 1} onClick={() => fetchData(page - 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                <FiChevronRight size={12} /> السابق
              </button>
              <button disabled={page >= pagination.pages} onClick={() => fetchData(page + 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page >= pagination.pages ? "default" : "pointer", opacity: page >= pagination.pages ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                التالي <FiChevronLeft size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Expandable detail row for recycle bin entry (shows snapshot) ───
function RecycleBinDetailRow({ entryId, colSpan }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await recycleBinApi.get(entryId);
        setEntry(r.data.entry);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [entryId]);

  const renderSnapshotPreview = (snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return null;
    const entries = Object.entries(snapshot).filter(([k, v]) => {
      // Skip giant fields and internal ones
      if (k === "raw_data" || k === "id") return false;
      if (v === null || v === undefined || v === "") return false;
      if (typeof v === "string" && v.length > 200) return false;
      return true;
    }).slice(0, 12);
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ fontSize: 11, padding: 6, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 4 }}>
            <div style={{ color: "#94a3b8", fontSize: 9, marginBottom: 2 }}>{k}</div>
            <div style={{ color: "#0f172a", fontWeight: 500, wordBreak: "break-word" }}>
              {typeof v === "object" ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 80)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 14, background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
        {loading ? (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>جاري التحميل...</div>
        ) : !entry ? (
          <div style={{ fontSize: 11, color: "#94a3b8" }}>تعذّر التحميل</div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 8 }}>📋 لقطة من السجل عند الحذف:</div>
            {renderSnapshotPreview(entry.snapshot)}
            {entry.restore_metadata && (
              <div style={{ marginTop: 8, fontSize: 10, color: "#64748b" }}>
                <strong>الاستراتيجية:</strong> {entry.restore_metadata.strategy}
                {entry.restore_metadata.table && <> · <strong>الجدول:</strong> {entry.restore_metadata.table}</>}
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LOGIN HISTORY TAB (v3.3.546)
// سجل تسجيل الدخول: من، متى، من أي IP/جهاز/متصفح، مدة الجلسة.
// ═══════════════════════════════════════════════════════════════════════
function LoginHistoryTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ q: "", device_type: "", date_from: "", date_to: "", active_only: false });
  // v3.3.547 — Timeline modal state
  const [timelineSessionId, setTimelineSessionId] = useState(null);
  const page = pagination.page;

  const fetchData = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 50 };
      if (filters.q) params.q = filters.q;
      if (filters.device_type) params.device_type = filters.device_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.active_only) params.active_only = "1";
      const r = await loginHistoryApi.list(params);
      setItems(r.data.items || []);
      setPagination({ page: r.data.page, pages: r.data.total_pages, total: r.data.total });
    } catch (e) {
      toast.error("فشل تحميل سجل الدخول");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await loginHistoryApi.stats();
      setStats(r.data);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchData(1); fetchStats(); }, [fetchData, fetchStats]);

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("ar-EG", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  const fmtDuration = (seconds, isActiveNow) => {
    if (isActiveNow) return <span style={{ color: "#16a34a", fontWeight: 700 }}>🟢 نشط الآن</span>;
    if (!seconds && seconds !== 0) return <span style={{ color: "#94a3b8" }}>—</span>;
    if (seconds < 60) return `${seconds} ث`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} د`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hours}س ${mins}د`;
  };

  const getDeviceIcon = (type) => {
    if (type === "Mobile") return <FiSmartphone size={14} />;
    if (type === "Tablet") return <FiTablet size={14} />;
    return <FiMonitor size={14} />;
  };

  return (
    <div>
      {/* Stats grid */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
          <StatCard label="إجمالي مرات الدخول" value={stats.total} icon="🔐" color="#0f172a" />
          <StatCard label="اليوم" value={stats.today} icon="📅" color="#2563eb" />
          <StatCard label="نشط الآن" value={stats.active_now} icon="🟢" color="#16a34a" />
          <StatCard label="آخر 7 أيام" value={stats.last_7_days} icon="📊" color="#7c3aed" />
          <StatCard label="عناوين IP فريدة (30ي)" value={stats.unique_ips_30d} icon="🌐" color="#d97706" />
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 250px", minWidth: 200 }}>
          <FiSearch size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input type="text" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
            placeholder="بحث بالإيميل، الاسم، IP، أو المتصفح..."
            style={{ width: "100%", padding: "8px 32px 8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12 }} />
        </div>
        <select value={filters.device_type}
          onChange={e => setFilters(f => ({ ...f, device_type: e.target.value }))}
          style={{ padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit", fontSize: 12 }}>
          <option value="">كل الأجهزة</option>
          <option value="Desktop">سطح المكتب</option>
          <option value="Mobile">جوال</option>
          <option value="Tablet">تابلت</option>
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
          <input type="checkbox" checked={filters.active_only}
            onChange={e => setFilters(f => ({ ...f, active_only: e.target.checked }))} />
          نشط الآن فقط
        </label>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
            <div style={{ fontSize: 13 }}>لا توجد سجلات</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={sty.th}>الوقت</th>
                  <th style={sty.th}>المستخدم</th>
                  <th style={sty.th}>IP</th>
                  <th style={sty.th}>الجهاز</th>
                  <th style={sty.th}>المتصفح</th>
                  <th style={sty.th}>النظام</th>
                  <th style={sty.th}>المدة</th>
                  <th style={sty.th}>التفاصيل</th>
                </tr>
              </thead>
              <tbody>
                {items.map(entry => (
                  <tr key={entry.id} style={{ background: entry.is_active_now ? "#f0fdf4" : "#fff" }}>
                    <td style={sty.td}>
                      <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>{fmtDate(entry.login_at)}</div>
                      {entry.logout_at && (
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>خرج: {fmtDate(entry.logout_at)}</div>
                      )}
                    </td>
                    <td style={sty.td}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{entry.user_display_name || "—"}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>{entry.user_email}</div>
                    </td>
                    <td style={sty.td}>
                      <span style={{ fontSize: 11, color: "#475569", fontFamily: "monospace" }}>
                        <FiGlobe size={10} style={{ marginLeft: 4, color: "#94a3b8" }} />
                        {entry.ip_address || "—"}
                      </span>
                    </td>
                    <td style={sty.td}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569" }}>
                        {getDeviceIcon(entry.device_type)} {entry.device_type || "—"}
                      </span>
                    </td>
                    <td style={sty.td}>
                      <span style={{ fontSize: 11, color: "#475569" }}>{entry.browser || "—"}</span>
                    </td>
                    <td style={sty.td}>
                      <span style={{ fontSize: 11, color: "#475569" }}>{entry.os || "—"}</span>
                    </td>
                    <td style={sty.td}>
                      <span style={{ fontSize: 11, color: "#475569" }}>
                        {fmtDuration(entry.duration_seconds, entry.is_active_now)}
                      </span>
                    </td>
                    <td style={sty.td}>
                      <button onClick={() => setTimelineSessionId(entry.id)} title="عرض timeline كامل للجلسة" style={{
                        padding: "4px 10px", background: "#ede9fe", color: "#6d28d9", border: "none",
                        borderRadius: 5, fontSize: 10, cursor: "pointer", fontFamily: "inherit",
                        fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4,
                      }}>
                        📅 Timeline
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #f1f5f9", background: "#fafbfc" }}>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              صفحة {page} من {pagination.pages} · {pagination.total} سجل
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button disabled={page <= 1} onClick={() => fetchData(page - 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                <FiChevronRight size={12} /> السابق
              </button>
              <button disabled={page >= pagination.pages} onClick={() => fetchData(page + 1)}
                style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 5, background: "#fff", cursor: page >= pagination.pages ? "default" : "pointer", opacity: page >= pagination.pages ? 0.4 : 1, fontFamily: "inherit", fontSize: 11 }}>
                التالي <FiChevronLeft size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top users / browsers section */}
      {stats && (stats.top_users?.length > 0 || stats.top_browsers?.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
          {stats.top_users?.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: "#0f172a" }}>👤 الأكثر دخولاً (30 يوم)</div>
              {stats.top_users.map((u, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, borderBottom: idx < stats.top_users.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ color: "#475569" }}>{u.user_display_name || u.user_email}</span>
                  <span style={{ color: "#0891b2", fontWeight: 700 }}>{u.login_count} مرة</span>
                </div>
              ))}
            </div>
          )}
          {stats.top_browsers?.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: "#0f172a" }}>🌐 المتصفحات الأكثر استخداماً (30 يوم)</div>
              {stats.top_browsers.map((b, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, borderBottom: idx < stats.top_browsers.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ color: "#475569" }}>{b.browser || "—"}</span>
                  <span style={{ color: "#7c3aed", fontWeight: 700 }}>{b.c} مرة</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* v3.3.547 — Session timeline modal */}
      {timelineSessionId && (
        <SessionTimelineModal sessionId={timelineSessionId} onClose={() => setTimelineSessionId(null)} />
      )}
    </div>
  );
}

// ─── Session Timeline Modal (v3.3.547) ───
// Shows page views + audit entries chronologically for one login session.
function SessionTimelineModal({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await loginHistoryApi.timeline(sessionId);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("فشل تحميل التايملاين");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const fmtTime = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("ar-EG", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };
  const fmtFullDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("ar-EG", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };
  const fmtDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "—";
    if (seconds < 60) return `${seconds} ث`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} د`;
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return `${h}س ${m}د`;
  };

  // Merge page_views and audit_entries into a single chronological list
  const events = data ? [
    ...(data.page_views || []).map(pv => ({
      type: "page_view",
      time: pv.viewed_at,
      data: pv,
    })),
    ...(data.audit_entries || []).map(au => ({
      type: "audit",
      time: au.created_at,
      data: au,
    })),
  ].sort((a, b) => new Date(a.time) - new Date(b.time)) : [];

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)",
      zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: 40, overflowY: "auto",
    }}>
      <div onClick={e => e.stopPropagation()} dir="rtl" style={{
        background: "#fff", borderRadius: 12, padding: 20, maxWidth: 900, width: "100%",
        boxShadow: "0 25px 50px rgba(0,0,0,0.25)", position: "relative",
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: "absolute", top: 12, left: 12,
          background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 20,
        }}>
          <FiX />
        </button>

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
          📅 timeline الجلسة
        </h3>
        <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 14 }}>
          كل ما قام به المستخدم خلال هذه الجلسة (صفحات فُتحت + إجراءات)
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
            جاري التحميل...
          </div>
        ) : !data ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
            لا توجد بيانات
          </div>
        ) : (
          <>
            {/* Session summary */}
            <div style={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#fff", borderRadius: 10, padding: 14, marginBottom: 14,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                {data.session.user_display_name || data.session.user_email}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, lineHeight: 1.8 }}>
                📅 الدخول: <strong>{fmtFullDate(data.session.login_at)}</strong><br />
                {data.session.logout_at && <>🚪 الخروج: <strong>{fmtFullDate(data.session.logout_at)}</strong><br /></>}
                🌐 IP: <strong style={{ fontFamily: "monospace" }}>{data.session.ip_address || "—"}</strong><br />
                💻 المتصفح: <strong>{data.session.browser || "—"}</strong> على <strong>{data.session.os || "—"}</strong> ({data.session.device_type})<br />
                📊 الصفحات: <strong>{data.counts.page_views}</strong> · الإجراءات: <strong>{data.counts.audit_entries}</strong>
              </div>
            </div>

            {/* Timeline */}
            {events.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                لا توجد أحداث في هذه الجلسة
              </div>
            ) : (
              <div style={{ position: "relative", paddingRight: 24 }}>
                {/* Vertical line */}
                <div style={{
                  position: "absolute", right: 12, top: 0, bottom: 0, width: 2,
                  background: "linear-gradient(to bottom, #16a34a, #2563eb, #7c3aed)",
                  borderRadius: 1,
                }} />
                {events.map((ev, idx) => {
                  const isPage = ev.type === "page_view";
                  const dot = isPage ? "#16a34a" : "#7c3aed";
                  return (
                    <div key={idx} style={{ position: "relative", marginBottom: 12 }}>
                      {/* Dot */}
                      <div style={{
                        position: "absolute", right: -8, top: 6,
                        width: 14, height: 14, borderRadius: "50%",
                        background: dot, border: "3px solid #fff",
                        boxShadow: "0 0 0 1px " + dot,
                      }} />
                      <div style={{
                        background: isPage ? "#f0fdf4" : "#faf5ff",
                        border: "1px solid " + (isPage ? "#bbf7d0" : "#e9d5ff"),
                        borderRadius: 8, padding: 10, marginRight: 20,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            {isPage ? (
                              <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>
                                  📄 فتح صفحة: {ev.data.page_title || ev.data.page_path}
                                </div>
                                <div style={{ fontSize: 10, color: "#16a34a", marginTop: 2, fontFamily: "monospace" }}>
                                  {ev.data.page_path}
                                </div>
                                {ev.data.duration_seconds != null && (
                                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                                    ⏱️ بقي عليها: <strong>{fmtDuration(ev.data.duration_seconds)}</strong>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#6d28d9" }}>
                                  ⚡ {ACTION_LABELS[ev.data.action] || ev.data.action}
                                  {ev.data.entity_label ? ` · ${ev.data.entity_label}` : ""}
                                </div>
                                <div style={{ fontSize: 10, color: "#7c3aed", marginTop: 2 }}>
                                  {MODULE_LABELS[ev.data.module] || ev.data.module}
                                  {ev.data.entity_id ? ` #${ev.data.entity_id}` : ""}
                                </div>
                                {ev.data.changes && Object.keys(ev.data.changes).length > 0 && (
                                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                                    🔄 تعديل <strong>{Object.keys(ev.data.changes).length}</strong> حقل
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: "#64748b", whiteSpace: "nowrap", fontFamily: "monospace" }}>
                            {fmtTime(ev.time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Small stat card used by LoginHistoryTab ───
function StatCard({ label, value, icon, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 18, fontWeight: 700, color }}>{value?.toLocaleString("ar-EG") || 0}</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SECURITY COMPLIANCE TAB (v3.3.553)
// تقييم الأمن السيبراني الذاتي - بناءً على NCA ECC + إشارات لـ SAMA / PDPL
// ═══════════════════════════════════════════════════════════════════════
function SecurityComplianceTab() {
  const [checklist, setChecklist] = useState([]);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [automatedResults, setAutomatedResults] = useState(null);
  const [automatedSummary, setAutomatedSummary] = useState(null);
  const [automatedScore, setAutomatedScore] = useState(null);
  const [runningAuto, setRunningAuto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [latestAssessment, setLatestAssessment] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [chR, latestR] = await Promise.all([
          securityApi.getChecklist(),
          securityApi.getLatest(),
        ]);
        setChecklist(chR.data.domains || []);
        if (latestR.data.assessment) {
          setLatestAssessment(latestR.data.assessment);
          if (latestR.data.assessment.answers) {
            setAnswers(latestR.data.assessment.answers);
          }
        }
      } catch (e) {
        toast.error("فشل تحميل قائمة التحقق");
      }
    })();
  }, []);

  const runAutomated = async () => {
    setRunningAuto(true);
    try {
      const r = await securityApi.runAutomated();
      setAutomatedResults(r.data.results);
      setAutomatedSummary(r.data.summary);
      setAutomatedScore(r.data.score);
      toast.success(`تم الفحص: ${r.data.score}% نتيجة آلية`, { duration: 4000 });
    } catch (e) {
      toast.error("فشل تشغيل الفحص الآلي");
    } finally {
      setRunningAuto(false);
    }
  };

  const setAnswer = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
  };

  // ─── Compute scores client-side for live feedback ───
  const computeScores = () => {
    const valueOf = (v) => v === "yes" ? 1 : v === "partial" ? 0.5 : v === "no" ? 0 : null;
    const domainScores = {};
    let totalEarned = 0;
    let totalPossible = 0;
    let totalAnswered = 0;
    let totalQuestions = 0;
    for (const d of checklist) {
      let earned = 0;
      let possible = 0;
      for (const it of d.items) {
        totalQuestions++;
        const v = valueOf(answers[it.id]);
        if (v === null) continue;
        totalAnswered++;
        earned += v * (it.weight || 1);
        possible += (it.weight || 1);
      }
      domainScores[d.id] = possible > 0 ? Math.round((earned / possible) * 1000) / 10 : null;
      totalEarned += earned;
      totalPossible += possible;
    }
    const overall = totalPossible > 0 ? Math.round((totalEarned / totalPossible) * 1000) / 10 : 0;
    return { domainScores, overall, totalAnswered, totalQuestions };
  };

  const { domainScores, overall, totalAnswered, totalQuestions } = computeScores();

  const handleSave = async () => {
    if (totalAnswered === 0) {
      toast.error("أجب على بعض الأسئلة على الأقل قبل الحفظ");
      return;
    }
    setSaving(true);
    try {
      const r = await securityApi.save({
        answers,
        notes: notes || undefined,
        automated_results: automatedResults || undefined,
      });
      toast.success(`تم الحفظ ✓ النتيجة: ${r.data.overall_score}%`, { duration: 5000 });
      // Refresh latest
      const latestR = await securityApi.getLatest();
      setLatestAssessment(latestR.data.assessment);
    } catch (e) {
      toast.error("فشل الحفظ: " + (e?.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString("ar-EG", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

  const scoreColor = (s) => {
    if (s == null) return "#94a3b8";
    if (s >= 80) return "#16a34a";
    if (s >= 60) return "#d97706";
    if (s >= 40) return "#dc2626";
    return "#991b1b";
  };
  const scoreLabel = (s) => {
    if (s == null) return "—";
    if (s >= 90) return "ممتاز";
    if (s >= 75) return "جيد جداً";
    if (s >= 60) return "جيد";
    if (s >= 40) return "يحتاج تحسين";
    return "يحتاج تحسين عاجل";
  };

  const statusIcon = (status) => {
    switch (status) {
      case "pass": return <FiCheckCircle size={16} style={{ color: "#16a34a" }} />;
      case "warn": return <FiAlertTriangle size={16} style={{ color: "#d97706" }} />;
      case "fail": return <FiXCircle size={16} style={{ color: "#dc2626" }} />;
      case "info": return <FiInfo size={16} style={{ color: "#0891b2" }} />;
      case "manual": return <FiSettings size={16} style={{ color: "#7c3aed" }} />;
      default: return null;
    }
  };
  const statusLabel = (status) => {
    switch (status) {
      case "pass": return "ناجح";
      case "warn": return "تحذير";
      case "fail": return "فاشل";
      case "info": return "معلومة";
      case "manual": return "فحص يدوي";
      default: return status;
    }
  };

  return (
    <div>
      {/* CRITICAL Disclaimer */}
      <div style={{
        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
        border: "2px solid #f59e0b",
        borderRadius: 10, padding: 14, marginBottom: 16,
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <FiAlertTriangle size={20} style={{ color: "#b45309", flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.7 }}>
          <strong style={{ fontSize: 13 }}>تنبيه مهم — قراءة إلزامية:</strong><br />
          هذا <strong>تقييم ذاتي إرشادي</strong> مبني على <strong>NCA ECC-1:2018</strong> (الضوابط الأساسية للأمن السيبراني السعودية)
          مع إشارات لـ <strong>SAMA Cyber Security Framework</strong> و <strong>PDPL</strong>.
          <br />
          <strong style={{ color: "#92400e" }}>هذا التقييم ليس:</strong>
          <ul style={{ margin: "4px 0", paddingRight: 18 }}>
            <li>شهادة compliance رسمية</li>
            <li>بديل عن Penetration Test (PT) أو Vulnerability Test (VT) الفعلي</li>
            <li>تدقيق من جهة معتمدة</li>
          </ul>
          <strong>PT/VT الحقيقي يتطلب جهة معتمدة</strong> (مثل STC Solutions، Cipher، Help AG) ولا يمكن "بضغطة زر".
        </div>
      </div>

      {/* Header: scores summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <ScoreCard
          label="النتيجة الإجمالية (التقييم الذاتي)"
          score={overall}
          subtitle={`${totalAnswered}/${totalQuestions} سؤال مُجاب`}
          color={scoreColor(overall)}
          status={scoreLabel(overall)}
        />
        <ScoreCard
          label="نتيجة الفحص الآلي"
          score={automatedScore}
          subtitle={automatedSummary ? `${automatedSummary.pass}/${automatedSummary.total} ناجح` : "لم يتم الفحص بعد"}
          color={scoreColor(automatedScore)}
          status={automatedScore != null ? scoreLabel(automatedScore) : "—"}
        />
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>آخر تقييم محفوظ</div>
          {latestAssessment ? (
            <>
              <div style={{ fontSize: 22, fontWeight: 700, color: scoreColor(latestAssessment.overall_score) }}>
                {latestAssessment.overall_score}%
              </div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                {fmtDate(latestAssessment.created_at)}<br />
                بواسطة: {latestAssessment.user_name || latestAssessment.user_email}
              </div>
            </>
          ) : <div style={{ fontSize: 13, color: "#94a3b8" }}>لم يُحفظ أي تقييم بعد</div>}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={runAutomated} disabled={runningAuto} style={{
          padding: "10px 18px",
          background: runningAuto ? "#cbd5e1" : "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
          color: "#fff", border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: runningAuto ? "wait" : "pointer",
          fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
          boxShadow: runningAuto ? "none" : "0 2px 6px rgba(8, 145, 178, 0.3)",
        }}>
          <FiPlayCircle size={14} /> {runningAuto ? "جاري الفحص..." : "تشغيل الفحص الآلي"}
        </button>
        <button onClick={handleSave} disabled={saving || totalAnswered === 0} style={{
          padding: "10px 18px",
          background: (saving || totalAnswered === 0) ? "#cbd5e1" : "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#fff", border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: (saving || totalAnswered === 0) ? "not-allowed" : "pointer",
          fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6,
          boxShadow: (saving || totalAnswered === 0) ? "none" : "0 2px 6px rgba(124, 58, 237, 0.3)",
        }}>
          <FiSave size={14} /> {saving ? "جاري الحفظ..." : "حفظ التقييم"}
        </button>
      </div>

      {/* Automated check results */}
      {automatedResults && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#0f172a" }}>
              🔬 نتائج الفحص الآلي ({automatedResults.length} فحص)
            </h3>
            <div style={{ fontSize: 10, color: "#64748b" }}>
              ✓ {automatedSummary.pass} ناجح · ⚠️ {automatedSummary.warn} تحذير · ✗ {automatedSummary.fail} فاشل · ℹ️ {automatedSummary.info + automatedSummary.manual} معلومة
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
            {automatedResults.map(r => (
              <div key={r.id} style={{
                padding: "8px 10px", borderRadius: 6,
                background: r.status === "pass" ? "#f0fdf4" :
                           r.status === "warn" ? "#fefce8" :
                           r.status === "fail" ? "#fef2f2" :
                           "#f1f5f9",
                border: "1px solid " + (
                  r.status === "pass" ? "#bbf7d0" :
                  r.status === "warn" ? "#fde68a" :
                  r.status === "fail" ? "#fecaca" :
                  "#e2e8f0"
                ),
                display: "flex", gap: 10, alignItems: "flex-start",
              }}>
                <div style={{ flexShrink: 0, marginTop: 1 }}>{statusIcon(r.status)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{r.details}</div>
                  {r.recommendation && (
                    <div style={{ fontSize: 11, color: "#b45309", marginTop: 4, fontStyle: "italic" }}>
                      💡 {r.recommendation}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: 9, padding: "2px 8px", borderRadius: 10,
                  background: r.status === "pass" ? "#dcfce7" : r.status === "warn" ? "#fef3c7" : r.status === "fail" ? "#fee2e2" : "#e0f2fe",
                  color: r.status === "pass" ? "#166534" : r.status === "warn" ? "#92400e" : r.status === "fail" ? "#991b1b" : "#075985",
                  fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
                }}>
                  {statusLabel(r.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual checklist by domain */}
      <div style={{ display: "grid", gap: 14 }}>
        {checklist.map(domain => {
          const dScore = domainScores[domain.id];
          return (
            <div key={domain.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#0f172a" }}>
                    {domain.icon} {domain.title}
                  </h3>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                    {domain.description} · مرجع: {domain.nca_ref}
                  </div>
                </div>
                {dScore != null && (
                  <div style={{ fontSize: 18, fontWeight: 700, color: scoreColor(dScore) }}>
                    {dScore}%
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                {domain.items.map(item => {
                  const v = answers[item.id];
                  return (
                    <div key={item.id} style={{
                      padding: 8, borderRadius: 6,
                      background: "#f8fafc", border: "1px solid #e2e8f0",
                      display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
                    }}>
                      <div style={{ flex: "1 1 280px", fontSize: 12, color: "#0f172a" }}>
                        {item.text}
                        {item.weight && item.weight > 1 && (
                          <span style={{ fontSize: 9, color: "#94a3b8", marginRight: 4 }}>· وزن {item.weight}</span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>
                        {[
                          { v: "yes", label: "نعم", color: "#16a34a" },
                          { v: "partial", label: "جزئي", color: "#d97706" },
                          { v: "no", label: "لا", color: "#dc2626" },
                          { v: "na", label: "لا ينطبق", color: "#64748b" },
                        ].map(opt => (
                          <button key={opt.v} onClick={() => setAnswer(item.id, opt.v)} style={{
                            padding: "4px 10px", fontSize: 11,
                            background: v === opt.v ? opt.color : "#fff",
                            color: v === opt.v ? "#fff" : "#475569",
                            border: "1px solid " + (v === opt.v ? opt.color : "#cbd5e1"),
                            borderRadius: 5, cursor: "pointer",
                            fontFamily: "inherit", fontWeight: v === opt.v ? 600 : 400,
                          }}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Notes */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginTop: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", display: "block", marginBottom: 6 }}>
          📝 ملاحظات إضافية (اختياري)
        </label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="أي ملاحظات أو تحفظات أو خطط للتحسين..."
          style={{
            width: "100%", minHeight: 80, padding: 10,
            border: "1px solid #e2e8f0", borderRadius: 6,
            fontFamily: "inherit", fontSize: 12, resize: "vertical",
          }} />
      </div>

      {/* Footer guidance */}
      <div style={{
        marginTop: 16, padding: 14,
        background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8,
        fontSize: 11, color: "#0c4a6e", lineHeight: 1.8,
      }}>
        <strong>📚 مراجع رسمية:</strong><br />
        • <a href="https://nca.gov.sa/legislations/ecc-controls" target="_blank" rel="noreferrer" style={{ color: "#0284c7" }}>NCA ECC-1:2018 - الضوابط الأساسية للأمن السيبراني</a><br />
        • <a href="https://www.sama.gov.sa/en-us/Laws/Pages/CyberSecurity.aspx" target="_blank" rel="noreferrer" style={{ color: "#0284c7" }}>SAMA Cyber Security Framework</a> (للمؤسسات المالية المرخصة فقط)<br />
        • <a href="https://sdaia.gov.sa/en/SDAIA/about/Pages/PDPLDescription.aspx" target="_blank" rel="noreferrer" style={{ color: "#0284c7" }}>PDPL - قانون حماية البيانات الشخصية</a><br />
        <strong>🎯 الخطوات التالية لتحسين الأمان:</strong><br />
        1. أكمل الفحص الآلي + التقييم اليدوي شهرياً<br />
        2. اطلب Penetration Test من جهة معتمدة سنوياً (مثل STC Solutions، Cipher، Help AG)<br />
        3. سجّل الشركة في NCA Haseen platform لتقييم أكثر شمولاً<br />
        4. درّب الموظفين على الوعي الأمني (Phishing simulation)<br />
        5. خطة استجابة للحوادث (Incident Response Plan) موثقة ومُختبَرة
      </div>
    </div>
  );
}

// ─── Small score card used in SecurityComplianceTab ───
function ScoreCard({ label, score, subtitle, color, status }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: score != null ? color : "#cbd5e1" }}>
          {score != null ? score : "—"}
        </span>
        {score != null && <span style={{ fontSize: 16, fontWeight: 600, color }}>%</span>}
      </div>
      <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>{subtitle}</div>
      {score != null && (
        <div style={{ fontSize: 11, color, fontWeight: 700, marginTop: 6 }}>{status}</div>
      )}
    </div>
  );
}
